import { useEffect, useMemo, useState } from "react";
import { MapPinned } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { LocationQueue } from "./LocationQueue";
import { PILMapPane, type PILMapItem, type SportCourt } from "./PILMapPane";
import { PILPhotosStrip, type PILPhoto } from "./PILPhotosStrip";
import { PILReviewForm, type PILForReview, type LocationMappingSummary } from "./PILReviewForm";

interface SpotReviewTabProps {
  collectionId: string;
}

export function SpotReviewTab({ collectionId }: SpotReviewTabProps) {
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedPilId, setSelectedPilId] = useState<string | null>(null);

  // Reset selection when collection changes
  useEffect(() => {
    setSelectedLocationId(null);
    setSelectedPilId(null);
  }, [collectionId]);

  const { data: details, isLoading: loadingDetails } =
    trpc.physicalMapping.getLocationDetails.useQuery(
      { locationId: selectedLocationId ?? "" },
      { enabled: !!selectedLocationId },
    );

  const { data: reviewPILs, isLoading: loadingReview } =
    trpc.physicalMapping.listPILsForReview.useQuery(
      {
        collectionId,
        locationId: selectedLocationId ?? "",
      },
      { enabled: !!selectedLocationId && !!collectionId },
    );

  const { data: courts } = trpc.sportCourts.listByLocation.useQuery(
    { locationId: selectedLocationId ?? "" },
    { enabled: !!selectedLocationId },
  );

  // Pick first PIL by default
  useEffect(() => {
    if (!reviewPILs || reviewPILs.length === 0) {
      setSelectedPilId(null);
      return;
    }
    const stillExists = reviewPILs.some((p: any) => p.id === selectedPilId);
    if (!stillExists) {
      setSelectedPilId(reviewPILs[0].id as string);
    }
  }, [reviewPILs, selectedPilId]);

  const pilMapItems: PILMapItem[] = useMemo(() => {
    if (!reviewPILs) return [];
    return (reviewPILs as any[]).map((p) => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      ordinal: p.ordinal,
      status: p.status,
    }));
  }, [reviewPILs]);

  const sportCourts: SportCourt[] = useMemo(
    () => (courts as SportCourt[] | undefined) ?? [],
    [courts],
  );

  const locationSports = useMemo(() => {
    const set = new Set<string>();
    for (const c of sportCourts) {
      if (c.sport_type) set.add(c.sport_type);
    }
    return Array.from(set);
  }, [sportCourts]);

  const selectedPil: PILForReview | null = useMemo(() => {
    if (!reviewPILs || !selectedPilId) return null;
    const p = (reviewPILs as any[]).find((x) => x.id === selectedPilId);
    if (!p) return null;
    return {
      id: p.id,
      ordinal: p.ordinal,
      status: p.status,
      is_viable: p.is_viable ?? null,
      general_comments: p.general_comments ?? null,
      admin_notes: p.admin_notes ?? null,
      admin_tier: p.admin_tier ?? null,
      submitted_at: p.submitted_at ?? null,
      rejection_reason: p.rejection_reason ?? null,
      role: (p.role ?? null) as "main" | "backup" | "none" | null,
      lockers: (p.lockers ?? []).map((l: any) => ({
        locker_index: l.locker_index,
        sport_type: l.sport_type,
      })),
    };
  }, [reviewPILs, selectedPilId]);

  const selectedPilPhotos: PILPhoto[] = useMemo(() => {
    if (!reviewPILs || !selectedPilId) return [];
    const p = (reviewPILs as any[]).find((x) => x.id === selectedPilId);
    return ((p?.photos ?? []) as any[]).map((ph) => ({
      id: ph.id as string,
      signedUrl: ph.url as string,
    }));
  }, [reviewPILs, selectedPilId]);

  const locationOverviewPhotos: PILPhoto[] = useMemo(() => {
    const all = (details?.location_photos ?? []) as any[];
    return all
      .filter((p) => p.kind === "overview")
      .map((p) => ({ id: p.id as string, signedUrl: p.url as string }));
  }, [details]);

  const locationDegradationPhotos: PILPhoto[] = useMemo(() => {
    const all = (details?.location_photos ?? []) as any[];
    return all
      .filter((p) => p.kind === "degradation")
      .map((p) => ({ id: p.id as string, signedUrl: p.url as string }));
  }, [details]);

  const { prevPilId, nextPilId } = useMemo(() => {
    if (!reviewPILs || !selectedPilId)
      return { prevPilId: null as string | null, nextPilId: null as string | null };
    const arr = reviewPILs as any[];
    const idx = arr.findIndex((p) => p.id === selectedPilId);
    return {
      prevPilId: idx > 0 ? (arr[idx - 1].id as string) : null,
      nextPilId: idx >= 0 && idx < arr.length - 1 ? (arr[idx + 1].id as string) : null,
    };
  }, [reviewPILs, selectedPilId]);

  const advanceAfterDecision = () => {
    // After approve/reject, the mutated PIL is now admin_approved or rejected —
    // so it will be filtered out of the refreshed listPILsForReview. We don't
    // need to cross collections; just advance to the next pending PIL, else null.
    if (!reviewPILs) return;
    const arr = reviewPILs as any[];
    const remaining = arr.filter((p) => p.id !== selectedPilId);
    if (remaining.length === 0) {
      setSelectedPilId(null);
      return;
    }
    const currentIdx = arr.findIndex((p) => p.id === selectedPilId);
    const next = arr[currentIdx + 1] ?? remaining[0];
    setSelectedPilId(next.id as string);
  };

  const locationSummary: LocationMappingSummary | null = details?.location
    ? {
        public_access: (details.location as any).mapping_public_access ?? null,
        open_24_7: (details.location as any).mapping_open_24_7 ?? null,
        opening_hours: (details.location as any).mapping_opening_hours ?? null,
        passing_by_visibility:
          (details.location as any).mapping_passing_by_visibility ?? null,
        nearby_habitations:
          (details.location as any).mapping_nearby_habitations ?? null,
        signs_of_degradation:
          (details.location as any).mapping_signs_of_degradation ?? null,
        janitors: (details.location as any).mapping_janitors ?? null,
        lighting: (details.location as any).mapping_lighting_present ?? null,
        dubious_people: (details.location as any).mapping_dubious_people ?? null,
        video_cameras: (details.location as any).mapping_video_cameras ?? null,
      }
    : null;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: location queue */}
      <div className="w-72 shrink-0 border-r overflow-y-auto">
        <div className="px-3 py-2 border-b text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Locations
        </div>
        <LocationQueue
          collectionId={collectionId}
          selectedLocationId={selectedLocationId}
          onSelect={(id) => {
            setSelectedLocationId(id);
            setSelectedPilId(null);
          }}
        />
      </div>

      {/* Center: map + photos */}
      <div className="flex-1 flex flex-col min-w-0 border-r">
        {!selectedLocationId ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MapPinned className="size-10 mx-auto mb-3 opacity-40" />
              <p className="text-base font-medium">Pick a location to review</p>
              <p className="text-sm mt-1">
                Choose a location from the queue on the left.
              </p>
            </div>
          </div>
        ) : loadingDetails || !details?.location ? (
          <div className="flex-1 p-4">
            <Skeleton className="h-full w-full" />
          </div>
        ) : (
          <>
            <div className="flex-1 relative min-h-0">
              <PILMapPane
                location={{
                  id: (details.location as any).id,
                  lat: (details.location as any).lat,
                  lng: (details.location as any).lng,
                  name: (details.location as any).name,
                  primary_type: (details.location as any).primary_type ?? null,
                }}
                pils={pilMapItems}
                courts={sportCourts}
                selectedPilId={selectedPilId}
                onSelectPil={setSelectedPilId}
              />
            </div>
            <div className="border-t shrink-0 bg-muted/20 max-h-[22rem] overflow-y-auto">
              {locationOverviewPhotos.length > 0 && (
                <div>
                  <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Location overview
                  </div>
                  <PILPhotosStrip photos={locationOverviewPhotos} />
                </div>
              )}
              {locationDegradationPhotos.length > 0 && (
                <div className="border-t">
                  <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Location degradation
                  </div>
                  <PILPhotosStrip photos={locationDegradationPhotos} />
                </div>
              )}
              <div
                className={
                  locationOverviewPhotos.length > 0 ||
                  locationDegradationPhotos.length > 0
                    ? "border-t"
                    : ""
                }
              >
                <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  PIL photos
                </div>
                <PILPhotosStrip photos={selectedPilPhotos} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right: review form */}
      <div className="w-[26rem] shrink-0 overflow-hidden flex flex-col">
        {!selectedLocationId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
            Select a location to start reviewing PILs.
          </div>
        ) : loadingReview ? (
          <div className="p-4 space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : !selectedPil || !locationSummary ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
            All PILs in this location have been reviewed.
            <br />
            Pick another location.
          </div>
        ) : (
          <PILReviewForm
            locationId={selectedLocationId}
            collectionId={collectionId}
            pil={selectedPil}
            locationSports={locationSports}
            locationSummary={locationSummary}
            prevPilId={prevPilId}
            nextPilId={nextPilId}
            onAdvanceAfterDecision={advanceAfterDecision}
            onNavigate={setSelectedPilId}
          />
        )}
      </div>
    </div>
  );
}
